import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Camera, Trash2, X } from 'lucide-react';
import Sheet from './Sheet';
import ProductImage from './ProductImage';
import { api, errorMessage } from '../lib/api';
import { boxLabel } from '../lib/boxes';
import { cn } from '../lib/utils';

const CameraScanner = lazy(() => import('./CameraScanner'));

/* Capture or correct a carton on the receiving floor: scan its EAN, measure
   it, weigh it. Saved straight to Odoo as a product.packaging.info row (Odoo
   assigns the box number and the internal barcode). `box` = edit mode. */
export default function BoxSheet({ line, box = null, prefillCode = '', onSaved, onDeleted, onClose }) {
  const editing = Boolean(box);
  const [form, setForm] = useState({
    barcode: editing ? box.vendor_barcode || '' : prefillCode,
    length: editing && box.length ? String(box.length) : '',
    width: editing && box.width ? String(box.width) : '',
    height: editing && box.height ? String(box.height) : '',
    weight: editing && box.weight ? String(box.weight) : '',
    description: editing ? box.description || '' : '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [camera, setCamera] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirm, setConfirm] = useState(true);
  const [noEan, setNoEan] = useState(Boolean(line?.no_vendor_ean)); // this supplier prints no EAN on the carton
  // product-level handling, ticked while measuring a new item
  const [pallet, setPallet] = useState(Boolean(line?.pallet));
  const [oversize, setOversize] = useState(Boolean(line?.oversize));
  const refs = { barcode: useRef(null), length: useRef(null), width: useRef(null), height: useRef(null), weight: useRef(null) };
  const order = ['barcode', 'length', 'width', 'height', 'weight'];

  useEffect(() => {
    (prefillCode || (editing && box.vendor_barcode) ? refs.length : refs.barcode).current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const next = (k) => (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const i = order.indexOf(k);
    if (i < order.length - 1) refs[order[i + 1]].current?.focus();
    else submit();
  };

  const index = editing ? line.boxes.findIndex((b) => b.id === box.id) : line?.boxes?.length || 0;
  const title = editing ? boxLabel(box, index) : `Box ${index + 1}`;
  const num = (k) => Number(String(form[k]).replace(',', '.'));
  const allDims = ['length', 'width', 'height', 'weight'].every((k) => num(k) > 0);
  const otherIncomplete = (line?.boxes || []).some((b) => (!editing || b.id !== box.id) && !(b.length && b.width && b.height && b.weight));
  const canSave = form.barcode.trim() || ['length', 'width', 'height', 'weight'].some((k) => form[k]);
  const noCode = !form.barcode.trim() && !noEan;

  const submit = async () => {
    if (busy || !canSave) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        barcode: form.barcode.trim(),
        length: form.length,
        width: form.width,
        height: form.height,
        weight: form.weight,
        description: form.description.trim(),
        confirm: confirm && allDims,
        no_vendor_ean: noEan ? true : line?.no_vendor_ean ? false : null,
      };
      const saved = editing ? await api.updateBox(box.id, payload) : await api.createBox({ product_id: line.product_id, ...payload });
      const flags = {};
      if (pallet !== Boolean(line?.pallet)) flags.shipped_on_pallet = pallet;
      if (oversize !== Boolean(line?.oversize)) flags.oversize = oversize;
      if (Object.keys(flags).length) await api.setFlags(line.product_id, flags);
      onSaved?.(saved);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      await api.deleteBox(box.id);
      onDeleted?.(box);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  // A plain function (not a nested component): a component defined inside the
  // render would remount its input on every keystroke and lose focus.
  const field = (k, label, { unit, inputMode = 'decimal', mono = false, placeholder = '' } = {}) => (
    <label className="block">
      <span className="text-[12px] font-semibold text-ink2">{label}</span>
      <span className="mt-0.5 flex h-12 items-center rounded-xl border border-line bg-surface px-3 focus-within:border-ink">
        <input
          ref={refs[k]}
          value={form[k]}
          onChange={set(k)}
          onKeyDown={next(k)}
          inputMode={inputMode}
          autoComplete="off"
          placeholder={placeholder}
          className={cn('min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-ink2/90', mono && 'font-mono tabular-nums')}
        />
        {unit && <span className="pl-2 text-[13px] text-ink2">{unit}</span>}
      </span>
    </label>
  );

  return (
    <Sheet open onClose={busy ? undefined : onClose} dismissible={!busy} label={editing ? 'Edit box' : 'Add a box'}>
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-start gap-3">
          <ProductImage src={line.image} alt="" className="h-14 w-14" />
          <div className="min-w-0 flex-1">
            <p className="font-cond text-[24px] font-bold leading-none">{title}</p>
            <p className="clamp-2 mt-1 text-[14px] leading-tight text-ink2">{line.name}</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>
        {!editing && line.boxes?.length > 0 && (
          <p className="mt-2 text-[13px] text-ink2">
            On file: {line.boxes.map((b, i) => boxLabel(b, i)).join(', ')}. This adds one more carton per unit.
          </p>
        )}

        <div className="mt-4 space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">{field('barcode', noEan ? 'Barcode, for the label or a kit (optional)' : 'Barcode on the carton', { inputMode: 'text', mono: true, placeholder: 'scan or type, Enter for next' })}</div>
            <button type="button" onClick={() => setCamera(true)} aria-label="Scan with the camera" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sunken active:bg-line">
              <Camera className="h-5 w-5" />
            </button>
          </div>
          <label className={cn('flex items-start gap-3 rounded-xl px-3 py-2.5', noEan ? 'tone-crossdock bg-tone-bg' : 'bg-sunken')}>
            <input type="checkbox" checked={noEan} onChange={(e) => setNoEan(e.target.checked)} className="mt-0.5 h-5 w-5 accent-current" />
            <span className="text-[13px]">
              <span className="font-semibold">This supplier prints no EAN on the carton</span>
              <span className="block text-ink2">Inny counts this product by hand, Odoo stops asking for a vendor barcode, and a label is printed after the receipt is validated. A code entered above is kept on the box for the label or a kit.</span>
            </span>
          </label>
          {noCode && !editing && (
            <p className="text-[13px] text-hold">No barcode yet: scan the carton, or tick the box above if this supplier never prints one. Otherwise Odoo refuses the receipt.</p>
          )}
          <div className="grid grid-cols-3 gap-2">
            {field('length', 'Length', { unit: 'cm' })}
            {field('width', 'Width', { unit: 'cm' })}
            {field('height', 'Height', { unit: 'cm' })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {field('weight', 'Weight', { unit: 'kg' })}
            <label className="block">
              <span className="text-[12px] font-semibold text-ink2">Label (optional)</span>
              <span className="mt-0.5 flex h-12 items-center rounded-xl border border-line bg-surface px-3 focus-within:border-ink">
                <input value={form.description} onChange={set('description')} placeholder="Shade, base…" className="min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-ink2/90" />
              </span>
            </label>
          </div>
          <div>
            <span className="text-[12px] font-semibold text-ink2">Handling (for the whole product)</span>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <label className={cn('flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-semibold', oversize ? 'tone-crossdock bg-tone-bg text-tone' : 'bg-sunken')}>
                <input type="checkbox" checked={oversize} onChange={(e) => setOversize(e.target.checked)} className="h-5 w-5 accent-current" />
                Oversize
              </label>
              <label className={cn('flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-semibold', pallet ? 'tone-stock bg-tone-bg text-tone' : 'bg-sunken')}>
                <input type="checkbox" checked={pallet} onChange={(e) => setPallet(e.target.checked)} className="h-5 w-5 accent-current" />
                Ships on pallet
              </label>
            </div>
          </div>
          <label className={cn('flex items-start gap-3 rounded-xl bg-sunken px-3 py-2.5', !allDims && 'opacity-50')}>
            <input type="checkbox" checked={confirm && allDims} disabled={!allDims} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5 h-5 w-5 accent-current" />
            <span className="text-[13px]">
              <span className="font-semibold">Measured on the floor: confirm the packaging</span>
              <span className="block text-ink2">
                {allDims
                  ? otherIncomplete
                    ? 'Saved now; the packaging is confirmed once the other boxes have size and weight too.'
                    : 'Marks this product\u2019s packaging as confirmed, so its volume and weight count from now on.'
                  : 'Needs all four numbers.'}
              </span>
            </span>
          </label>
        </div>

        {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}

        <button type="button" onClick={submit} disabled={busy || !canSave} className="mt-4 h-14 w-full rounded-xl bg-ink text-[16px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
          {busy ? 'Saving to Odoo…' : editing ? 'Save changes' : `Save box ${index + 1}`}
        </button>
        {prefillCode && !editing && <p className="mt-2 text-center text-[13px] text-ink2">Saving also counts the carton you just scanned.</p>}

        {editing && !confirmDelete && (
          <button type="button" onClick={() => setConfirmDelete(true)} disabled={busy} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[14px] font-semibold text-alert active:bg-alert-bg">
            <Trash2 className="h-4 w-4" /> Remove this box
          </button>
        )}
        {editing && confirmDelete && (
          <div className="mt-3 rounded-xl bg-alert-bg p-3">
            <p className="text-[14px] text-alert">Remove {title} from this product in Odoo? Its EAN will no longer scan.</p>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={remove} disabled={busy} className="flex-1 rounded-xl bg-alert py-2.5 text-[14px] font-semibold text-bg active:opacity-80">
                Yes, remove
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={busy} className="flex-1 rounded-xl bg-surface py-2.5 text-[14px] font-semibold active:bg-sunken">
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
      {camera && (
        <Suspense fallback={<div className="fixed inset-0 z-50 bg-black" />}>
          <CameraScanner
            onScan={(code) => {
              setForm((f) => ({ ...f, barcode: code }));
              setCamera(false);
              setTimeout(() => refs.length.current?.focus(), 50);
            }}
            onClose={() => setCamera(false)}
          />
        </Suspense>
      )}
    </Sheet>
  );
}
